import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patternAPI, accountAPI } from '../services/api';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, Cell
} from 'recharts';

type Snapshot = {
  ts: string;
  balance: number;
  equity: number;
  floating: number;
  drawdown_pct: number;
};

type Daily = {
  ymd: string;
  trade_count: number;
  lots_total: number;
  net_pnl: number;
  scalping_score: number;
  smg: string;
};

type Threshold = {
  peakBalance: number;
  minEquity: number;
  maxDrawdown: number;
  worstDayPnl: number;
  worstDayPct: number;
  recommendedThreshold: number;
};

const formatMoney = (v: number) =>
  `$${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const AccountDetailPage: React.FC = () => {
  const { login } = useParams<{ login: string }>();
  const navigate = useNavigate();
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [dailyData, setDailyData] = useState<Daily[]>([]);
  const [threshold, setThreshold] = useState<Threshold | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [activeTab, setActiveTab] = useState<'equity' | 'drawdown' | 'pnl'>('equity');

  const fetchData = async () => {
    if (!login) return;
    setLoading(true);
    try {
      // 빠른 API 먼저 로드
      const [dailyRes, thresholdRes, accRes] = await Promise.all([
        patternAPI.getAccountDaily(Number(login)),
        patternAPI.getAccountThreshold(Number(login)),
        accountAPI.getAll(),
      ]);
      setDailyData(dailyRes.data);
      setThreshold(thresholdRes.data);
      const registered = accRes.data.some((a: any) => String(a.accountId) === login);
      setIsRegistered(registered);
      setLoading(false);

      // snapshot은 별도로 로드 (느림)
      try {
        const snapshotRes = await patternAPI.getAccountSnapshot(Number(login));
        const raw: Snapshot[] = snapshotRes.data;
        const sampled = raw.filter((_: Snapshot, i: number) => i % 60 === 0);
        setSnapshots(sampled);
      } catch (snapErr) {
        console.error('Snapshot 로드 실패:', snapErr);
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  const handleRegister = async () => {
    if (!window.confirm(`계좌 ${login}을 B북으로 등록하시겠습니까?\n추천 임계값: ${threshold?.recommendedThreshold}%`)) return;
    setRegistering(true);
    try {
      await accountAPI.register({
        accountId: login,
        isBBook: true,
        isActive: true,
        thresholdEntry: threshold?.recommendedThreshold ?? -5,
        thresholdMin: 0,
        thresholdMax: -20,
        symbolMode: 'ALL',
      });
      setIsRegistered(true);
      alert('B북 등록 완료!');
    } catch (err: any) {
      alert(err.response?.data?.message || '등록 실패');
    } finally { setRegistering(false); }
  };

  const equityChartData = snapshots.map(s => ({
    time: s.ts.substring(5, 16),
    equity: s.equity,
    balance: s.balance,
  }));

  const drawdownChartData = snapshots.map(s => ({
    time: s.ts.substring(5, 16),
    drawdown: s.drawdown_pct,
  }));

  const pnlChartData = dailyData.map(d => ({
    date: d.ymd.substring(5),
    pnl: d.net_pnl,
    trades: d.trade_count,
  }));

  const totalPnl = dailyData.reduce((sum, d) => sum + d.net_pnl, 0);
  const winDays = dailyData.filter(d => d.net_pnl > 0).length;
  const lossDays = dailyData.filter(d => d.net_pnl < 0).length;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button style={styles.backBtn} onClick={() => navigate(-1)}>← 뒤로</button>
          <div>
            <h1 style={styles.title}>계좌 <span style={styles.loginBadge}>{login}</span> 상세 분석</h1>
            <p style={styles.subtitle}>최근 90일 거래 패턴 분석</p>
          </div>
        </div>
        {!isRegistered ? (
          <button style={styles.registerBtn} onClick={handleRegister} disabled={registering}>
            {registering ? '등록 중...' : `🔖 B북 등록 (추천 임계값: ${threshold?.recommendedThreshold}%)`}
          </button>
        ) : (
          <span style={styles.registeredBadge}>✅ 등록됨</span>
        )}
      </div>

      {loading ? <div style={styles.loading}>데이터 로딩 중...</div> : (
        <>
          {threshold && (
            <div style={styles.thresholdCard}>
              <div style={styles.thresholdTitle}>📊 임계값 분석</div>
              <div style={styles.thresholdGrid}>
                <div style={styles.thresholdItem}>
                  <div style={styles.thresholdLabel}>Peak Balance</div>
                  <div style={styles.thresholdValue}>{formatMoney(threshold.peakBalance)}</div>
                </div>
                <div style={styles.thresholdItem}>
                  <div style={styles.thresholdLabel}>최저 Equity</div>
                  <div style={styles.thresholdValue}>{formatMoney(threshold.minEquity)}</div>
                </div>
                <div style={styles.thresholdItem}>
                  <div style={styles.thresholdLabel}>최대 Drawdown</div>
                  <div style={{ ...styles.thresholdValue, color: '#ff6b6b' }}>{threshold.maxDrawdown}%</div>
                </div>
                <div style={styles.thresholdItem}>
                  <div style={styles.thresholdLabel}>최악 하루 손실</div>
                  <div style={{ ...styles.thresholdValue, color: '#ff6b6b' }}>{formatMoney(threshold.worstDayPnl)}</div>
                </div>
                <div style={{ ...styles.thresholdItem, borderColor: 'rgba(102,126,234,0.5)', background: 'rgba(102,126,234,0.1)' }}>
                  <div style={styles.thresholdLabel}>📌 추천 임계값</div>
                  <div style={{ ...styles.thresholdValue, color: '#667eea', fontSize: '24px' }}>
                    {threshold.recommendedThreshold}%
                  </div>
                </div>
              </div>
            </div>
          )}

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>총 거래일</div>
              <div style={styles.summaryValue}>{dailyData.length}일</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>수익일 / 손실일</div>
              <div style={styles.summaryValue}>
                <span style={{ color: '#52c41a' }}>{winDays}</span>
                <span style={{ color: 'rgba(255,255,255,0.3)' }}> / </span>
                <span style={{ color: '#ff6b6b' }}>{lossDays}</span>
              </div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>총 손익</div>
              <div style={{ ...styles.summaryValue, color: totalPnl > 0 ? '#52c41a' : '#ff6b6b' }}>
                {formatMoney(totalPnl)}
              </div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>총 거래수</div>
              <div style={styles.summaryValue}>{dailyData.reduce((s, d) => s + d.trade_count, 0)}건</div>
            </div>
          </div>

          <div style={styles.chartCard}>
            <div style={styles.tabGroup}>
              {([['equity', 'Equity / Balance'], ['drawdown', 'Drawdown %'], ['pnl', '일별 PnL']] as const).map(([key, label]) => (
                <button
                  key={key}
                  style={activeTab === key ? { ...styles.tab, ...styles.tabActive } : styles.tab}
                  onClick={() => setActiveTab(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {activeTab === 'equity' && (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={equityChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="time" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} interval={Math.floor(equityChartData.length / 8)} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} tickFormatter={(v: number) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} formatter={(v: any) => [`$${Number(v).toLocaleString()}`, '']} />
                  <Line type="monotone" dataKey="balance" stroke="#667eea" dot={false} strokeWidth={1.5} name="Balance" />
                  <Line type="monotone" dataKey="equity" stroke="#00d2ff" dot={false} strokeWidth={1.5} name="Equity" />
                </LineChart>
              </ResponsiveContainer>
            )}

            {activeTab === 'drawdown' && (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={drawdownChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="time" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} interval={Math.floor(drawdownChartData.length / 8)} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} tickFormatter={(v: number) => `${v}%`} />
                  <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} formatter={(v: any) => [`${Number(v).toFixed(2)}%`, 'Drawdown']} />
                  <ReferenceLine y={threshold?.recommendedThreshold} stroke="#667eea" strokeDasharray="5 5" label={{ value: '추천 임계값', fill: '#667eea', fontSize: 11 }} />
                  <ReferenceLine y={-5} stroke="#f7971e" strokeDasharray="3 3" label={{ value: '-5%', fill: '#f7971e', fontSize: 10 }} />
                  <Line type="monotone" dataKey="drawdown" stroke="#ff6b6b" dot={false} strokeWidth={1.5} name="Drawdown" />
                </LineChart>
              </ResponsiveContainer>
            )}

            {activeTab === 'pnl' && (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={pnlChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} tickFormatter={(v: number) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} formatter={(v: any) => [`$${Number(v).toLocaleString()}`, 'PnL']} />
                  <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" />
                  <Bar dataKey="pnl" name="일별 PnL" radius={[2, 2, 0, 0]}>
                    {pnlChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.pnl > 0 ? '#52c41a' : '#ff6b6b'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div style={styles.tableCard}>
            <h3 style={styles.tableTitle}>일별 거래 내역</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>날짜</th>
                    <th style={styles.th}>거래수</th>
                    <th style={styles.th}>Lots</th>
                    <th style={styles.th}>Net PnL</th>
                    <th style={styles.th}>스타일</th>
                    <th style={styles.th}>스캘핑점수</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyData.map(d => (
                    <tr key={d.ymd} style={styles.tr}>
                      <td style={styles.td}>{d.ymd}</td>
                      <td style={styles.td}>{d.trade_count}건</td>
                      <td style={styles.td}>{d.lots_total}</td>
                      <td style={styles.td}>
                        <span style={{ color: d.net_pnl > 0 ? '#52c41a' : '#ff6b6b', fontWeight: 600 }}>
                          {formatMoney(d.net_pnl)}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700,
                          background: d.smg === 'S' ? 'rgba(255,107,107,0.2)' : d.smg === 'M' ? 'rgba(247,151,30,0.2)' : 'rgba(102,126,234,0.2)',
                          color: d.smg === 'S' ? '#ff6b6b' : d.smg === 'M' ? '#f7971e' : '#667eea'
                        }}>
                          {d.smg === 'S' ? '스캘핑' : d.smg === 'M' ? '미디엄' : '장기'}
                        </span>
                      </td>
                      <td style={styles.td}>{d.scalping_score}점</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { color: '#fff', maxWidth: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: 700, color: '#fff', margin: '0 0 4px' },
  subtitle: { color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: 0 },
  loginBadge: { fontFamily: 'monospace', background: 'rgba(102,126,234,0.2)', padding: '2px 10px', borderRadius: '6px', color: '#667eea' },
  backBtn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '8px 16px', color: 'rgba(255,255,255,0.7)', fontSize: '13px', cursor: 'pointer' },
  registerBtn: { padding: '10px 20px', background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },
  registeredBadge: { padding: '8px 16px', background: 'rgba(82,196,26,0.15)', border: '1px solid rgba(82,196,26,0.3)', borderRadius: '8px', color: '#52c41a', fontSize: '14px' },
  loading: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
  thresholdCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '20px', marginBottom: '20px' },
  thresholdTitle: { fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: '16px' },
  thresholdGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' },
  thresholdItem: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '12px', textAlign: 'center' },
  thresholdLabel: { color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginBottom: '6px' },
  thresholdValue: { fontSize: '18px', fontWeight: 700, color: '#fff' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' },
  summaryCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '16px', textAlign: 'center' },
  summaryLabel: { color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginBottom: '6px' },
  summaryValue: { fontSize: '20px', fontWeight: 700, color: '#fff' },
  chartCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '20px', marginBottom: '20px' },
  tabGroup: { display: 'flex', gap: '8px', marginBottom: '20px' },
  tab: { padding: '8px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'rgba(255,255,255,0.5)', fontSize: '13px', cursor: 'pointer' },
  tabActive: { background: 'rgba(102,126,234,0.2)', borderColor: '#667eea', color: '#667eea' },
  tableCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '20px' },
  tableTitle: { fontSize: '16px', fontWeight: 600, color: '#fff', margin: '0 0 16px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '10px 16px', textAlign: 'left', color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.05)' },
  td: { padding: '10px 16px', color: 'rgba(255,255,255,0.8)', fontSize: '14px' },
};

export default AccountDetailPage;
